import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import config from '../../config';

// Speech Therapy Department Blogs
const STATIC_SPEECH_NEURODIVERSITY_BLOG = {
  id: 'speech-neurodiversity-blog-post',
  title: 'Embracing Neurodiversity in Speech Therapy: Understanding the Field and Its Impact',
  description: 'Discover how neurodiversity-affirming practices in speech and language therapy foster authentic communication, respect individual communication styles, and empower children.',
  main_image: '/assets/images/services/CIRCLE-(369-369).jpg',
  date: '2026-03-10T00:00:00.000Z',
  username: 'RHCA Speech Therapy Team',
  department: 'Speech Therapy'
};

const STATIC_AAC_AWARENESS_BLOG = {
  id: 'aac-awareness-blog-post',
  title: 'AAC Awareness Month',
  description: 'Celebrating Augmentative and Alternative Communication (AAC) Awareness Month: Highlighting multimodal communication tools and empowering non-speaking voices.',
  main_image: '/assets/images/services/Main-(770-377).jpg',
  date: '2026-03-05T00:00:00.000Z',
  username: 'RHCA Speech Therapy Team',
  department: 'Speech Therapy'
};

// Insights Department Blogs
const STATIC_RHCA_BLOG = {
  id: 'rhca-blog-post',
  title: 'A New Ray of Hope Begins at Electronics City',
  description: 'Ray of Hope Center for Autism (RHCA) marked a significant milestone with the launch of its new center at RR Towers...',
  main_image: 'https://images.unsplash.com/photo-1527525443983-6e60c75fff46?w=1200&q=80',
  date: '2025-12-13T00:00:00.000Z',
  username: 'Rachel George',
  department: 'Insights'
};

const STATIC_CAREGIVER_BLOG = {
  id: 'caregiver-support-blog-post',
  title: 'Supporting the Supporters: Caregiver Burnout in Autism',
  description: 'Behind every therapy session, IEP meeting, and progress milestone is a caregiver. But what happens when the caregiver runs on empty?',
  main_image: '/assets/images/blog/hero-mother-exhausted.jpeg',
  date: '2026-02-15T00:00:00.000Z',
  username: 'Rachel George',
  department: 'Insights'
};

const STATIC_UDID_BLOG = {
  id: 'udid-blog-post',
  title: "The Card Many Families Don't Know They Need: Understanding the UDID Card",
  description: 'A comprehensive guide for parents on the Unique Disability ID (UDID) card in India — benefits, application steps, state schemes, and common misconceptions.',
  main_image: '/assets/images/blog/electronic770-427.jpg',
  date: '2026-02-20T00:00:00.000Z',
  username: 'Keziah George',
  department: 'Insights'
};

const STATIC_ABA_EVOLVED_BLOG = {
  id: 'aba-evolved-blog-post',
  title: "ABA Didn't Need to Be Abandoned. It Needed to Evolve.",
  description: 'Many families describe ABA as a life-changing intervention. Explore how Applied Behavior Analysis has evolved from conformity to focusing on individual quality of life.',
  main_image: '/images/fish-tree.jpeg',
  date: '2026-03-01T00:00:00.000Z',
  username: 'Keziah George',
  department: 'Insights'
};

// ABA Department Blogs
const STATIC_ABA_REIMAGINED_BLOG = {
  id: 'aba-reimagined-blog-post',
  title: "ABA, Reimagined: Growing With What We've Learned",
  description: 'Imagine teaching a fish to climb a tree. Explore how conversations around neurodiversity are helping reshape autism intervention and therapy goals.',
  main_image: '/assets/images/blog/counselling-session.jpeg',
  date: '2026-02-25T00:00:00.000Z',
  username: 'Keziah George',
  department: 'ABA'
};

const STATIC_BLOGS = [
  // Speech Therapy
  STATIC_SPEECH_NEURODIVERSITY_BLOG,
  STATIC_AAC_AWARENESS_BLOG,
  // Insights
  STATIC_RHCA_BLOG,
  STATIC_CAREGIVER_BLOG,
  STATIC_UDID_BLOG,
  STATIC_ABA_EVOLVED_BLOG,
  // ABA
  STATIC_ABA_REIMAGINED_BLOG
];

const CATEGORIES = ['All', 'Speech Therapy', 'Insights', 'ABA'];

function BlogsPageSession() {
  const [blogs, setBlogs] = useState(STATIC_BLOGS);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    const fetchBlogs = async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/blogs/api/blogs`, { signal: controller.signal });
        const data = await response.json();

        if (data.success) {
          setBlogs([...STATIC_BLOGS, ...data.data]);
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          console.error('Fetch aborted due to timeout');
        } else {
          console.error('Failed to fetch dynamic blogs:', err);
        }
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    fetchBlogs();
    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleString('default', { month: 'short' })
    };
  };

  const getBlogImageSrc = (mainImage) => {
    if (!mainImage) return '/assets/images/blog/counselling-session.jpeg';
    if (mainImage.startsWith('http') || mainImage.startsWith('/') || mainImage.startsWith('assets') || mainImage.startsWith('images')) {
      return mainImage;
    }
    return `${config.apiBaseUrl}/uploads/${mainImage}`;
  };

  const filteredBlogs = selectedCategory === 'All'
    ? blogs
    : blogs.filter(b => b.department === selectedCategory);

  if (loading) {
    return <div className="container text-center py-5">Loading blogs...</div>;
  }

  return (
    <section className="blog-one py-5">
      <div className="container">
        {/* Department Filter Tabs */}
        <div className="d-flex justify-content-center flex-wrap mb-5 gap-2" style={{ gap: '10px' }}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`btn ${selectedCategory === cat ? 'btn-primary' : 'btn-outline-primary'}`}
              style={{
                borderRadius: '30px',
                padding: '8px 24px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                backgroundColor: selectedCategory === cat ? '#166534' : 'transparent',
                borderColor: '#166534',
                color: selectedCategory === cat ? '#FFF' : '#166534'
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {filteredBlogs.length === 0 ? (
          <div className="container text-center py-5">No blogs found in this department.</div>
        ) : (
          <div className="row">
            {filteredBlogs.map((blog, index) => {
              const formattedDate = formatDate(blog.date);

              return (
                <div
                  key={blog.id}
                  className="col-xl-4 col-lg-4 col-md-6 mb-4 wow fadeInUp"
                  data-wow-delay={`${(index + 1) * 100}ms`}
                >
                  <div className="blog-one__single shadow-sm" style={{ borderRadius: '15px', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div className="blog-one__img-box" style={{ position: 'relative' }}>
                      <div className="blog-one__img">
                        <img
                          src={getBlogImageSrc(blog.main_image)}
                          alt={blog.title}
                          style={{ height: '240px', width: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.src = '/assets/images/blog/counselling-session.jpeg';
                          }}
                        />
                        <Link to={`/blog-details/${blog.id}`}>
                          <span className="blog-one__plus"></span>
                        </Link>
                        <div className="blog-one__date">
                          <p>{formattedDate.day}<br />{formattedDate.month}</p>
                        </div>
                        {blog.department && (
                          <span 
                            style={{
                              position: 'absolute',
                              top: '15px',
                              right: '15px',
                              backgroundColor: '#166534',
                              color: '#FFF',
                              fontSize: '0.75rem',
                              fontWeight: '700',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}
                          >
                            {blog.department}
                          </span>
                        )}
                      </div>
                      <div className="blog-one__content-top" style={{ padding: '20px 20px 10px' }}>
                        <p className="blog-one__content-sub-title mb-1" style={{ fontSize: '0.85rem', color: '#6B7280' }}>by {blog.username}</p>
                        <h3 className="blog-one__content-title" style={{ fontSize: '1.15rem', fontWeight: '700', lineHeight: '1.4' }}>
                          <Link to={`/blog-details/${blog.id}`}>{blog.title}</Link>
                        </h3>
                      </div>
                    </div>
                    <div className="blog-one__content" style={{ padding: '0 20px 20px', marginTop: 'auto' }}>
                      <p style={{ fontSize: '0.9rem', color: '#4B5563', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {blog.description}
                      </p>
                      <div className="blog-one__comment-and-btn mt-3">
                        <div className="blog-one__btn">
                          <Link to={`/blog-details/${blog.id}`} style={{ fontWeight: '600', color: '#166534' }}>
                            <span className="icon-right-arrow"></span> Read More
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export default BlogsPageSession;